import React, {useEffect} from 'react';
import './AppsFeed.css';
import {APP_STORE_URL as NTB_APP_STORE, GOOGLE_PLAY_URL as NTB_GOOGLE_PLAY} from './products/nexttrainboston/links';

// The compact list of our apps that the Info screens of our apps show in a web view
// (https://answersolutions.net/#apps_feed). Each row opens the app's store page for the
// viewer's platform, or its page on this site when there is no listing for that platform.
// An app hides itself with ?skip=<id>, e.g. #apps_feed?skip=nexttrainboston.
const SITE = 'https://answersolutions.net/';

const APPS = [
    {
        id: 'agentsatwork', name: 'Agents At Work', icon: '/agents_at_work_icon.png',
        tagline: 'Watch and steer your AI coding agents from your phone.',
        ios: 'https://apps.apple.com/us/app/agents-at-work-you-can-go/id6761508632',
        android: 'https://play.google.com/store/apps/details?id=com.answersolutions.agentsatwork',
        page: '#agents-at-work-product',
    },
    {
        id: 'sigmaspeaks', name: 'Sigma Speaks', icon: '/sigma_speaks_artwork.png',
        tagline: 'Daily audio market news for retail investors, as a podcast.',
        // The apps are in beta, so the feed offers the podcast: Apple Podcasts on iPhone,
        // Spotify on Android and on the web.
        podcast: {
            ios: 'https://podcasts.apple.com/us/podcast/sigma-speaks/id1851266467',
            other: 'https://open.spotify.com/show/7hz2cM5DfbbTv26ppMbfdR',
        },
        page: '#sigma-speaks-product',
    },
    {
        id: 'runandread', name: 'Run & Read', icon: '/runAndRead128.png',
        tagline: 'Text to speech player for EPUB, PDF books and any text.',
        ios: 'https://apps.apple.com/us/app/run-read-listen-on-the-go/id6741396289',
        android: 'https://play.google.com/store/apps/details?id=com.answersolutions.runandread',
        page: '#run-andRead-product',
    },
    {
        id: 'timeslots', name: 'Time Slots', icon: '/timeslots128.png',
        tagline: 'Share your interview availability with one link.',
        web: 'https://timeslots.pro',
        page: '#time-slots-product',
    },
    {
        id: 'upwrite', name: 'Up Write AI', icon: '/upwrite192.png',
        tagline: 'A keyboard that fixes grammar and spelling as you type.',
        ios: 'https://apps.apple.com/us/app/upwrite-ai-proofread-keyboard/id6463730152',
        android: 'https://play.google.com/store/apps/details?id=com.answersolutions.upwriteai',
        page: '#up-write-product',
    },
    {
        id: 'studycards', name: 'StudyCards', icon: '/study_cards.png',
        tagline: 'Talking flashcards you make yourself or let AI generate.',
        ios: 'https://apps.apple.com/us/app/study-cards-help-to-memorize/id1436913731',
        android: 'https://play.google.com/store/apps/details?id=com.nes.studycards',
        page: '#study-cards-product',
    },
    {
        id: 'photodj', name: 'PhotoDj', icon: '/photo_dj.png',
        tagline: 'A slideshow of your phone\'s photos on the TV.',
        ios: 'https://apps.apple.com/us/app/photodj/id1325400635',
        android: 'https://play.google.com/store/apps/details?id=com.nes.pdj.photodj',
        page: '#photo-dj-product',
    },
    {
        id: 'callrecorder', name: 'A Call Recorder', icon: '/acra.png',
        tagline: 'Records regular phone calls in the best possible quality.',
        ios: 'https://apps.apple.com/us/app/a-call-recorder/id1622051987',
        android: 'https://play.google.com/store/apps/details?id=com.answersolutions.recordacall',
        page: '#a-call-recorder-product',
    },
    {
        id: 'nexttrainboston', name: 'Next Train Boston', icon: '/next_train_boston_icon.png',
        tagline: 'MBTA commuter rail and ferry times, offline.',
        ios: NTB_APP_STORE,
        android: NTB_GOOGLE_PLAY,
        page: '#next-train-boston-product',
    },
    {
        id: 'translator', name: 'Speech Translator', icon: '/voicebridge192.png',
        tagline: 'Speak anywhere in the world and understand anyone.',
        ios: 'https://apps.apple.com/us/app/speech-translator-be-heard/id6479205508',
        android: 'https://play.google.com/store/apps/details?id=com.answersolutions.talkwise.voicebridge',
        page: '#voice-translator-product',
    },
    {
        id: 'wisetalk', name: 'WiseTalk', icon: '/wt_logo192.png',
        tagline: 'A voice assistant powered by AI. Just talk.',
        ios: 'https://apps.apple.com/us/app/wisetalk-ai-powered-voice-chat/id6447212540',
        android: 'https://play.google.com/store/apps/details?id=com.answersolutions.talkwise',
        page: '#wise-talk-product',
    },
    {
        id: 'airsync', name: 'AirSync for Stickies', icon: '/airstickies.png',
        tagline: 'Your Mac\'s Stickies on your phone.',
        ios: 'https://apps.apple.com/us/app/airsync-your-stickies/id554182458',
        android: 'https://play.google.com/store/apps/details?id=com.umnes.stickies2go',
        page: '#air-sync-product',
    },
    {
        id: 'babymonitor', name: 'Baby Monitor WiFi', icon: '/app_baby_watcher.png',
        tagline: 'One phone watches, the other one listens.',
        ios: 'https://apps.apple.com/us/app/baby-watcher-eye-wi-fi/id930583309',
        android: 'https://play.google.com/store/apps/details?id=com.ansol.selfiepro',
    },
];

function platform() {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/Android/i.test(ua)) return 'android';
    // iPadOS reports a Mac user agent; touch points tell it apart.
    if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
    return 'web';
}

function skipped() {
    const query = window.location.hash.split('?')[1] || window.location.search.slice(1);
    const value = new URLSearchParams(query).get('skip');
    return value ? value.split(',') : [];
}

/** The link a row opens and its button: a store ("Get"), a podcast ("Listen"), a web app ("Open") or our page ("View"). */
function target(app, where) {
    if (app.podcast) {
        return {href: where === 'ios' ? app.podcast.ios : app.podcast.other, label: 'Listen'};
    }
    const store = where === 'ios' ? app.ios : where === 'android' ? app.android : null;
    if (store) return {href: store, label: 'Get'};
    if (app.web) return {href: app.web, label: 'Open'};
    return {href: SITE + (app.page || ''), label: 'View'};
}

export default function AppsFeed() {
    // Inside an app's web view the page is the whole view: white, no margins.
    useEffect(() => {
        const {background, margin} = document.body.style;
        document.body.style.background = '#ffffff';
        document.body.style.margin = '0';
        return () => {
            document.body.style.background = background;
            document.body.style.margin = margin;
        };
    }, []);
    const where = platform();
    const skip = skipped();
    return <div className="apps-feed">
        <div className="apps-feed-title">More apps by AnswerSolutions</div>
        {APPS.filter(app => !skip.includes(app.id)).map(app => {
            const {href, label} = target(app, where);
            return <a className="apps-feed-row" key={app.id} href={href} target="_blank" rel="noopener noreferrer">
                <img className="apps-feed-icon" src={app.icon} alt=""/>
                <div className="apps-feed-text">
                    <div className="apps-feed-name">{app.name}</div>
                    <div className="apps-feed-tagline">{app.tagline}</div>
                </div>
                <span className="apps-feed-get">{label}</span>
            </a>;
        })}
        <div className="apps-feed-footer">
            <a href={SITE} target="_blank" rel="noopener noreferrer">answersolutions.net</a>
        </div>
    </div>;
}
